// Shared class strings + inline style values for the mobile table-selection screen (Figma 1283-10 /
// 1283-17212). Colours are hard-coded hex to match the mock, consistent with the surrounding files.

export const TS_STYLES = {
  // The mobile sheet Paper is auto-height (capped at calc(100vh - 60px)); pin the screen to that
  // cap so the flex-1 map fills the sheet instead of collapsing to zero height.
  root: "flex h-[calc(100vh-60px)] max-h-[calc(100vh-60px)] min-h-0 flex-col bg-white",

  header: "relative flex h-[50px] shrink-0 items-center justify-center border-b border-[#e5e6ec] px-5",
  headerTitle: "text-[16px] font-normal leading-4 text-[#19191b]",
  headerBackBtn: "absolute left-5 flex h-8 w-8 items-center justify-center text-[#171717]",
  headerCloseBtn: "absolute right-5 flex h-8 w-8 items-center justify-center text-[#171717]",

  tabsRow: "flex shrink-0 items-center justify-between border-b border-[#e5e6ec] bg-[#f5f5f5] px-5",
  tabsGroup: "flex items-center gap-6 overflow-x-auto",
  tab: "relative shrink-0 border-0 bg-transparent px-0 py-3 text-[12px] leading-none outline-none",
  tabActive: "font-medium text-[#374151]",
  tabInactive: "font-normal text-[#374151]",
  // Sits flush on the tab row's bottom edge (classic tab indicator), hugging the label width.
  tabUnderline: "absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-[#2596fa]",
  infoBtn: "flex h-8 w-8 shrink-0 items-center justify-center text-[#374151]",

  mapArea: "relative flex min-h-0 flex-1 flex-col",

  // Floating sheet over the map (Figma 1283-6812): rounded top + soft up-shadow, no flat top border.
  footer: "shrink-0 rounded-t-[20px] bg-white shadow-[0_-5px_7.5px_rgba(0,0,0,0.05)]",
  footerInner: "px-5 pb-[24px] pt-3",

  pillsRow: "flex items-stretch gap-4",

  // Filled pill (a committed dimension): blue, headline + subtitle, trailing clear (×) segment.
  pillFilled: "flex h-10 flex-1 items-stretch overflow-hidden rounded-md border border-[#2176c1] bg-[#2596fa]",
  pillConflict: "border-[#bf4141] bg-[#ff5d5d]",
  pillFilledBody: "flex flex-1 items-center gap-2 pl-2.5",
  pillFilledIcon: "h-3.5 w-3.5 shrink-0 text-white",
  pillFilledHeadline: "text-[12px] font-medium leading-tight text-white",
  pillFilledSub: "text-[8px] leading-tight text-white/80",
  // Ring shown while the filled time pill's start/end picker is open (Figma 1283-13709).
  pillFilledOpen: "border-[2px] border-[#99d0ff]",
  pillTrailing: "flex w-10 shrink-0 items-center justify-center text-white",
  // The clear (×) glyph sits inside a darker circular badge in the trailing segment (Figma 1283-10).
  pillTrailingBadge: "flex h-6 w-6 items-center justify-center rounded-full bg-black/20",

  // Dropdown pill (an unset dimension): white, opens a panel.
  pillDropdown: "flex h-10 flex-1 items-stretch overflow-hidden rounded-md border border-[#e1e1e1] bg-white",
  pillDropdownOpen: "border-[2px] border-[#99d0ff]",
  pillDropdownBody: "flex flex-1 items-center gap-2 pl-2.5",
  pillDropdownIcon: "h-3.5 w-3.5 shrink-0 text-[#5d5d5d]",
  pillDropdownHeadline: "text-[12px] font-medium leading-tight text-[#374151]",
  pillDropdownSub: "text-[8px] leading-tight text-[#929baa]",
  pillDropdownTrailing: "flex w-10 shrink-0 items-center justify-center text-[#5d5d5d]",

  cta: "mt-3 flex h-[50px] w-full items-center justify-center gap-2 rounded-md text-[16px] font-medium text-white",
  ctaLogo: "mt-3 flex justify-center",

  // Grouped table dropdown panel (Figma 1283-17212): opens upward, 192px wide.
  tablePanel:
    "absolute bottom-full right-0 z-30 mb-2 max-h-[min(60vh,500px)] w-[192px] overflow-y-auto rounded-md border-[0.5px] border-[#e1e1e1] bg-white py-1.5 shadow-[4px_4px_10px_rgba(0,0,0,0.04)]",
  tableGroupHeader: "px-4 py-1.5 text-[16px] font-medium leading-tight text-black",
  tableRow: "w-full px-4 py-2 text-left text-[14px] font-normal leading-none text-[#5d5d5d]",
  tableRowDisabled: "opacity-40",
  tableGroupDivider: "mx-4 my-1 h-px bg-[#e1e1e1]",

  // Two-column start/end datetime picker (Figma 1283-13709): opens upward, 192px = two 96px columns.
  timePanel:
    "absolute bottom-full left-0 z-30 mb-2 w-[192px] overflow-hidden rounded-md border-[0.5px] border-[#e1e1e1] bg-white shadow-[4px_4px_10px_rgba(0,0,0,0.04)]",
  timePanelHeader: "flex border-b border-[#e1e1e1] bg-[#fbfbfb]",
  timePanelHeaderCell: "flex h-[47px] flex-1 items-center justify-center text-[14px] font-normal text-black",
  timePanelColumns: "flex",
  timePanelColumn: "max-h-[280px] flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  timePanelColumnDivider: "border-l border-[#e1e1e1]",
  // Row states are independent visual axes; selected wins over on-the-hour, disabled dims the base.
  timeRow: "flex h-[35px] w-full items-center justify-center text-[14px] leading-none",
  timeRowNormal: "font-light text-[#5d5d5d]",
  timeRowHour: "font-bold text-[#171717]",
  timeRowSelected: "bg-[#edf4fd] font-medium text-[#2596fa]",
  timeRowDisabled: "opacity-30",

  // "Best available picks" empty-state section (Figma 1283-51228): a light section title above a
  // horizontally-scrollable row of green (table + free-window) chips, each with a circular `+` handle.
  bestPicksSlot: "mb-3",
  bestPicksTitle: "mb-2 text-[14px] font-normal leading-normal text-[#7a7e8c]",
  bestPicksScroller: "-mx-5 flex gap-3 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  bestPicksChip: "flex h-[42px] shrink-0 items-center gap-3 rounded-[6px] border border-[#449f53] bg-[#69c580] px-3",
  bestPicksChipText: "flex flex-col justify-center whitespace-nowrap text-left",
  bestPicksChipName: "text-[12px] font-medium leading-tight text-white",
  bestPicksChipWindow: "text-[10px] font-normal leading-tight text-white",
  bestPicksChipPlus: "flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#449f53] text-white",
} as const

export const CTA_GRADIENT = "linear-gradient(180deg, #2596fa 0%, #165994 100%)"
export const CTA_BORDER = "1px solid #3478b7"

export const MAP_STATE_COLORS = {
  freeTableColor: "#6abc77",
  highlightOverlayOpacity: 0.6,
  disabledTableOpacity: 0.4,
} as const

// Availability-timeline palette (Figma 1283-6812 / 1283-7658). Hard-coded hex to match the mock,
// consistent with the surrounding files.
export const TIMELINE_COLORS = {
  track: "#f5f7f9",
  divider: "#d9d9d9",
  axisLabel: "#5f6774",
  bookedFill: "#d9d9d9",
  bookedBorder: "#aeb2b9",
  confirmedFill: "#2596fa",
  confirmedBorder: "#2176c1",
  proposedFill: "#69c580",
  proposedBorder: "#4faa61",
  proposedHandle: "#449f53",
  conflictFill: "#ff5d5d",
  conflictBorder: "#bf4141",
} as const
